import React, {useCallback, useEffect, useMemo, useReducer, useState} from "react";
import {useDebounceCallback} from "usehooks-ts";
import {
    deleteAreaEffect,
    getAreaEffects,
    saveAreaEffect,
} from "@/api/EncounterApi.ts";
import {AreaEffectContext, fromType} from "./AreaEffectHooks.ts";
import {useEncounter} from "@/encounter/EncounterHooks.ts";
import {AreaEffect, AreaEffectType} from "@/encounter/model/AreaEffect.ts";
import {useScreenCenter} from "@/tabletop/TableTopHooks.ts";
import type {AreaEffectUpdatePersistence} from "@/encounter/effect/AreaEffectHooks.ts";
import {AreaEffectEvent, useAreaEffectRealtime} from "@/encounter/effect/useAreaEffectRealtime.ts";
import {useDungeon} from "@/encounter/dungeon/DungeonHooks.ts";
import {useBackgroundErrorNotification} from "@/api/useBackgroundErrorNotification.ts";

interface AreaOfEffectProviderProps {
    children: React.ReactNode
}

type AreaEffectAction =
    | {type: "replace"; effects: AreaEffect[]}
    | {type: "event"; event: AreaEffectEvent};

function reducer(
    state: ReadonlyMap<string, AreaEffect>,
    action: AreaEffectAction,
): ReadonlyMap<string, AreaEffect> {
    if (action.type === "replace") {
        return new Map(action.effects.map(effect => [effect.id, effect]));
    }

    const {upserted, removedId} = action.event;
    if (!upserted && !removedId) return state;

    const updated = new Map(state);
    if (upserted) updated.set(upserted.id, upserted);
    if (removedId) updated.delete(removedId);
    return updated;
}

export default function AreaEffectProvider({children}: AreaOfEffectProviderProps) {
    const encounter = useEncounter();
    const {report, recover} = useBackgroundErrorNotification();
    const subscribeToUpdates = useAreaEffectRealtime(encounter.id);
    const center = useScreenCenter()
    const {dungeonSettings} = useDungeon();
    const [effectsById, dispatch] = useReducer(reducer, new Map<string, AreaEffect>());
    const [initialLoad, setInitialLoad] = useState<{encounterId?: string; error: boolean}>({error: false});
    const effects = useMemo(() => Array.from(effectsById.values()), [effectsById]);
    const initialLoading = initialLoad.encounterId !== encounter.id;

    useEffect(() => {
        dispatch({type: "replace", effects: []});

        let active = true;
        let initialSnapshotLoaded = false;
        const bufferedEvents: AreaEffectEvent[] = [];

        const unsubscribe = subscribeToUpdates(event => {
            if (!active) return;
            if (initialSnapshotLoaded) dispatch({type: "event", event});
            else bufferedEvents.push(event);
        });

        const finishInitialLoad = (snapshot: AreaEffect[], error = false) => {
            if (!active) return;
            dispatch({type: "replace", effects: snapshot});
            bufferedEvents.forEach(event => dispatch({type: "event", event}));
            bufferedEvents.length = 0;
            initialSnapshotLoaded = true;
            setInitialLoad({encounterId: encounter.id, error});
        };

        void getAreaEffects(encounter).then(finishInitialLoad).catch(() => finishInitialLoad([], true));

        return () => {
            active = false;
            unsubscribe();
        };
    }, [encounter, subscribeToUpdates]);

    const updateServer = useCallback(async (updated: AreaEffect) => {
        const saved = await saveAreaEffect(encounter, updated);
        dispatch({type: "event", event: {upserted: saved}});
    }, [encounter]);

    const updateInBackground = useCallback(async (updated: AreaEffect) => {
        try {
            await updateServer(updated);
            recover();
        } catch (error) {
            report(error, "Der Flächeneffekt konnte nicht gespeichert werden.");
        }
    }, [recover, report, updateServer]);
    const debouncedUpdate = useDebounceCallback(updateInBackground, 100);

    useEffect(() => () => debouncedUpdate.cancel(), [debouncedUpdate]);

    const updateEffect = (
        updated: AreaEffect,
        persistence: AreaEffectUpdatePersistence = "debounced",
    ) => {
        dispatch({type: "event", event: {upserted: updated}});
        if (persistence === "immediate") {
            debouncedUpdate.cancel();
            void updateInBackground(updated);
        } else {
            debouncedUpdate(updated);
        }
    }

    const addEffect = async (type: AreaEffectType) => {
        const newAreaEffect = fromType(type, center, dungeonSettings.levelId);
        const saved = await saveAreaEffect(encounter, newAreaEffect);
        dispatch({type: "event", event: {upserted: saved}});
    };

    const removeEffect = async (remove: AreaEffect) => {
        debouncedUpdate.cancel();
        const removedId = await deleteAreaEffect(encounter, remove.id);
        dispatch({type: "event", event: {removedId}});
    }

    const areaEffectReturn = {
        effects,
        initialLoading,
        updateEffect,
        addEffect,
        removeEffect
    }


    if (initialLoad.error) throw new Error("Flächeneffekte konnten nicht geladen werden.");

    return (
        <AreaEffectContext.Provider value={areaEffectReturn}>
            {children}
        </AreaEffectContext.Provider>
    )

}
